import { io, Socket } from "socket.io-client";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ReactNode,
  useState,
  useRef,
  useCallback,
  useContext,
  useEffect,
} from "react";

import { apiConfig } from "../../configs/api";
import { AuthenticationService } from "../../services/api/authentication";
import { ChatContext } from "./context";
import {
  ChatContextType,
  Message,
  AgentState,
  SQLQuery,
  DocSearch,
  ChartGenerated,
  ReasoningStep,
  ChatHistoryItem,
  ToolCall,
  ToolCallStatus,
  DisplayMetadata,
  GenericTableData,
} from "./type";
import { TOOL_DISPLAY_CONFIG } from "./toolDisplayConfig";
import { SOCKET_EVENT_TO_TOOL, TOOL_CALL_EVENTS } from "./toolEventMapping";
import {
  buildChatConversationScope,
  createChatConversationId,
} from "./conversationIdentity";
import { LayoutContext } from "../layout/context";
import { Run } from "../../services/appsync/types";
import { useCustomer } from "../app/provider";
import { VersionedDataContext } from "../versioned_data/context";

// Initial agent state
const initialAgentState: AgentState = {
  status: "idle",
  currentIteration: 0,
  maxIterations: 15,
  reasoning: "",
  streamedText: "",
  reasoningHistory: [],
  sqlQueries: [],
  docSearches: [],
  charts: [],
  toolCalls: [],
  genericTables: [],
};

const { Provider } = ChatContext;

const ServerURL = {
  socket: `${apiConfig.chatGateway.protocol}${apiConfig.chatGateway.host}`,
} as const;

const toSafeString = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === "number") return String(value);
  return undefined;
};

const getRunInfo = (run: Run | null) =>
  run
    ? {
        input_id: run.runSettings.inputId,
        workflow_id: run.runSettings.workflowId,
        organization_id: run.runSettings.organizationId,
        settings_name_and_version: run.runSettings.settingsNameAndVersion,
        run_attempt_id: run.runSettings.runAttemptId,
        name: toSafeString(run.name),
        display_id: toSafeString(run.displayId),
      }
    : null;

const MAX_GENERIC_TABLE_ROWS = 100;

const getObjectRows = (value: unknown): Record<string, unknown>[] => {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is Record<string, unknown> =>
      typeof item === "object" && item !== null && !Array.isArray(item)
  );
};

const buildGenericTable = (
  title: string,
  rows: Record<string, unknown>[],
  opts: {
    source?: string;
    summary?: string;
    preferredColumns?: string[];
  } = {}
): GenericTableData | null => {
  if (rows.length === 0) return null;

  const firstRow = rows.find((row) => Object.keys(row).length > 0);
  if (!firstRow) return null;

  const inferredColumns = Object.keys(firstRow);
  const preferred = opts.preferredColumns ?? [];
  const orderedPreferred = preferred.filter((col) => inferredColumns.includes(col));
  const remaining = inferredColumns.filter((col) => !orderedPreferred.includes(col));
  const columns = [...orderedPreferred, ...remaining];
  if (columns.length === 0) return null;

  return {
    title,
    columns,
    rows: rows.slice(0, MAX_GENERIC_TABLE_ROWS),
    source: opts.source,
    summary: opts.summary,
  };
};

const getTableFingerprint = (table: GenericTableData): string => {
  const first = table.rows[0] ?? {};
  const firstKey = JSON.stringify(first);
  return `${table.title}|${table.source ?? ""}|${table.columns.join(",")}|${table.rows.length}|${firstKey}`;
};

const extractGenericTableFromEvent = (
  eventType: string,
  data: Record<string, unknown>
): GenericTableData | null => {
  if (eventType === "ai_at_risk_skus") {
    const rows = getObjectRows(data.at_risk_skus);
    const atRiskCount = (data.summary as { at_risk_count?: number } | undefined)?.at_risk_count;
    const summary =
      typeof atRiskCount === "number" ? `${atRiskCount} at-risk SKU/location pairs` : undefined;
    return buildGenericTable("At-Risk SKUs", rows, {
      source: "supply_diagnosis.find_at_risk_skus",
      summary,
      preferredColumns: [
        "sku",
        "location",
        "risk_type",
        "stockout_date",
        "days_until_risk",
        "days_until_stockout",
        "severity",
        "total_unfulfilled_demand",
      ],
    });
  }

  if (eventType === "ai_supply_walk") {
    const rows = getObjectRows(data.data);
    return buildGenericTable("Supply Walk", rows, {
      source: "supply_diagnosis.get_supply_walk_for_sku",
      preferredColumns: [
        "datetime_week",
        "week",
        "measure",
        "value",
        "starting_inventory",
        "ending_inventory",
      ],
    });
  }

  if (eventType === "ai_po_history") {
    const rows = getObjectRows(data.purchase_orders);
    return buildGenericTable("Purchase Order History", rows, {
      source: "supply_diagnosis.get_po_history_for_sku",
      preferredColumns: [
        "po_number",
        "status",
        "delivery_date",
        "quantity",
        "unit_cost",
        "total_cost",
      ],
    });
  }

  if (eventType === "ai_po_coverage") {
    const rows = getObjectRows(data.purchase_orders);
    return buildGenericTable("PO Coverage", rows, {
      source: "supply_diagnosis.get_po_coverage_for_sku",
      preferredColumns: [
        "status",
        "supplier",
        "placement_date",
        "expected_arrival",
        "actual_arrival",
        "days_late",
        "quantity",
        "po_match_confidence",
      ],
    });
  }

  if (eventType === "ai_sku_master_attributes") {
    const multiRows = getObjectRows(data.multi_location_results);
    const rows = multiRows.length > 0 ? multiRows : getObjectRows([data]);
    const summary =
      multiRows.length > 0
        ? `Evaluated ${multiRows.length} locations`
        : undefined;
    return buildGenericTable("SKU Master Attributes", rows, {
      source: "supply_diagnosis.get_sku_master_attributes",
      summary,
      preferredColumns: [
        "sku",
        "location",
        "purchasing_eligibility",
        "is_purchasable",
        "is_conditionally_purchasable",
        "supplier",
        "supplier_assigned",
        "lead_time_days",
        "moq",
        "lifecycle_status",
        "blockers",
      ],
    });
  }

  if (eventType === "ai_actual_demand") {
    const rows = getObjectRows(data.actuals);
    return buildGenericTable("Actual Demand", rows, {
      source: "supply_diagnosis.get_actual_demand_for_sku",
      preferredColumns: ["total_quantity", "daily_average", "trend"],
    });
  }

  if (eventType === "ai_supplier_stats") {
    const rows = getObjectRows([data]);
    return buildGenericTable("Supplier Delivery Stats", rows, {
      source: "supply_diagnosis.get_supplier_delivery_stats",
      preferredColumns: [
        "supplier",
        "total_pos_analyzed",
        "delivered_pos",
        "on_time_rate",
        "avg_days_late",
        "median_days_late",
        "max_days_late",
      ],
    });
  }

  if (eventType === "ai_inventory_position") {
    const rows = getObjectRows([data]);
    return buildGenericTable("Inventory Position", rows, {
      source: "supply_diagnosis.get_inventory_position",
      preferredColumns: [
        "sku",
        "location",
        "system_starting_inventory",
        "actual_on_hand",
        "discrepancy",
        "discrepancy_pct",
        "is_significant",
        "system_overestimates",
      ],
    });
  }

  if (eventType === "ai_supply_runs_listed") {
    const rows = getObjectRows(data.runs);
    return buildGenericTable("Supply Runs", rows, {
      source: "supply_diagnosis.list_supply_runs",
      preferredColumns: ["input_id", "created_at", "workflow_id", "status"],
    });
  }

  return null;
};

const isRecoverableError = (toolName: string, errorText: string): boolean => {
  const lower = errorText.toLowerCase();
  if (toolName === 'get_supplier_delivery_stats') {
    return (
      lower.includes('multiple suppliers found') ||
      lower.includes('specify supplier explicitly')
    );
  }
  return false;
};

// Helper to create a ToolCall from socket event
const createToolCallFromEvent = (
  eventType: string,
  data: Record<string, unknown>,
): ToolCall => {
  const toolName = SOCKET_EVENT_TO_TOOL[eventType];
  const config = TOOL_DISPLAY_CONFIG[toolName];
  const iteration = (data.iteration as number) ?? 0;
  const metadata = (data.metadata as Record<string, unknown>) ?? {};
  const rawError = data.error ? String(data.error) : '';
  const recoverableError = rawError.length > 0 && isRecoverableError(toolName, rawError);

  // Extract common parameters from various event shapes
  const parameters: Record<string, unknown> = {};
  if (data.sku) parameters.sku = data.sku;
  if (data.location) parameters.location = data.location;
  if (!data.location && data.requested_location) parameters.location = data.requested_location;
  if (data.scenario) parameters.scenario = data.scenario;
  if (data.query_parameters) Object.assign(parameters, data.query_parameters);
  if (data.risk_type) parameters.risk_type = data.risk_type;
  if (data.horizon_days) parameters.horizon_days = data.horizon_days;

  // Determine status based on error presence
  const status: ToolCallStatus = data.error && !recoverableError ? 'error' : 'success';

  // Format result summary
  const resultSummary = recoverableError
    ? 'Refining parameters and retrying'
    : data.error
    ? String(data.error)
    : config?.resultFormatter?.(data) ?? 'Completed';

  // Extract display_metadata if present
  const displayMetadata = data.display_metadata as DisplayMetadata | undefined;

  return {
    id: `${toolName}-${iteration}-${Date.now()}`,
    toolName,
    displayName: config?.label ?? toolName,
    activeDisplayName: config?.activeLabel ?? `Running ${toolName}`,
    category: config?.category ?? 'transform',
    status,
    startTime: performance.now(),
    endTime: performance.now(),
    duration: 0,
    parameters,
    metadata: {
      runId: (metadata.run_id as string) ?? (metadata.runId as string),
      runTimestamp: (metadata.run_timestamp as string) ?? (metadata.execution_timestamp as string),
      organizationId: (metadata.organization_id as string) ?? (metadata.organizationId as string),
    },
    result: {
      summary: resultSummary,
      raw: data,
    },
    error: recoverableError ? undefined : (data.error as string | undefined),
    iteration,
    displayMetadata,
  };
};

export const ChatProvider = (props: { children: ReactNode }): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const socketRef = useRef<Socket | null>(null);
  const socketCustomerIdRef = useRef<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const conversationScopeRef = useRef<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [chatContext, setChatContext] = useState<string | null>(null);
  const [agentState, setAgentState] = useState<AgentState>(initialAgentState);
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);
  const [queuedMessages, setQueuedMessages] = useState(0);
  const [queuedPrompts, setQueuedPrompts] = useState<string[]>([]);
  const [isStopRequested, setIsStopRequested] = useState(false);

  // Refs to track current agent state for the done handler
  const agentStateRef = useRef<AgentState>(initialAgentState);
  const queuedPromptsRef = useRef<string[]>([]);
  const { comparedRun, setEditedRun, setComparedRun } =
    useContext(VersionedDataContext);
  const { setIsComparing } = useContext(LayoutContext);

  const authToken = AuthenticationService.getIdToken();
  const { selectedCustomerId, whoIAm } = useCustomer();

  const appendSentMessage = useCallback((content: string) => {
    const message: Message = {
      type: "sent",
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, message]);
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      const trimmedContent = content.trim();
      if (!trimmedContent) return;

      if (!chatContext) {
        console.error("No chat context available!");
        return;
      }

      if (!socketRef.current?.connected) {
        console.log("Socket not connected, attempting to reconnect...");
        await socketRef.current?.connect();
      }

      const isBusy =
        isAwaitingResponse || agentStateRef.current.status === "streaming";

      setIsAwaitingResponse(true);
      if (!conversationIdRef.current) {
        conversationIdRef.current = createChatConversationId();
      }

      // Keep queued prompts near the input. Add to transcript only when execution actually starts.
      if (isBusy) {
        setQueuedPrompts((prev) => {
          const next = [...prev, trimmedContent];
          queuedPromptsRef.current = next;
          return next;
        });
      } else {
        appendSentMessage(trimmedContent);
      }

      // Emit to Bedrock Agent chat handler
      socketRef.current?.emit(
        "ai_chat_message",
        JSON.stringify({
          message_content: trimmedContent,
          conversation_id: conversationIdRef.current,
        })
      );
    },
    [appendSentMessage, chatContext, isAwaitingResponse]
  );

  const stopCurrentResponse = useCallback((clearQueue: boolean = false) => {
    if (!socketRef.current?.connected) {
      return;
    }
    setIsStopRequested(true);
    socketRef.current.emit(
      "stop_ai_chat",
      JSON.stringify({
        clear_queue: clearQueue,
      })
    );
  }, []);

  const connectChat = useCallback(
    (run: Run | null, context: string) => {
      console.log("Initializing socket connection");

      if (!run) {
        console.error("No run available!");
        return;
      }

      if (!authToken) {
        console.error("No auth token available!");
        return;
      }
      const activeCustomerId =
        selectedCustomerId ?? run.runSettings.organizationId;

      // Cleanup previous socket if it exists
      if (socketRef.current) {
        console.log("Cleaning up previous socket");
        socketRef.current.disconnect();
        socketRef.current.removeAllListeners();
      }

      // Reset all chat states before starting new session
      setIsConnected(false);
      setMessages([]);
      setIsAwaitingResponse(false);
      setChatSessionId(null);
      setChatContext(context);
      setQueuedMessages(0);
      setQueuedPrompts([]);
      queuedPromptsRef.current = [];
      setIsStopRequested(false);
      // Reset agent state to prevent stale data from previous session
      setAgentState(initialAgentState);
      agentStateRef.current = initialAgentState;
      socketCustomerIdRef.current = activeCustomerId;

      const runInfo = getRunInfo(run);
      const conversationScope = buildChatConversationScope({
        customerId: activeCustomerId,
        context,
        runInfo,
      });
      const conversationId = createChatConversationId();
      conversationIdRef.current = conversationId;
      conversationScopeRef.current = conversationScope;

      // Create new socket with connection options
      const socket = io(ServerURL.socket, {
        auth: {
          cognito_id_token: authToken,
          customer_id: activeCustomerId,
          source: "client-frontend",
          context,
          conversation_id: conversationId,
          conversation_scope: conversationScope,
          main_run_settings: JSON.stringify(runInfo),
          user_email: whoIAm?.email || activeCustomerId,
        },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
        transports: ["websocket", "polling"],
      });

      socketRef.current = socket;

      // Socket event handlers
      socket.on("connect", () => {
        console.log("Socket connected:", socket.id);

        if (socketRef.current && socketRef.current.id) {
          if (context === "forecast") {
            setEditedRun(run);
            setComparedRun(run);
            navigate({
              pathname: location.pathname.includes("_preview")
                ? location.pathname
                : location.pathname + "_preview",
              search: location.search,
            });
          }
          setIsConnected(true);
          setChatContext(context);
          setChatSessionId(socketRef.current.id);
        }
      });

      socket.on("disconnect", (reason) => {
        console.log("Socket disconnected:", reason);
        const intentionallyClosed = reason === "io client disconnect";

        if (intentionallyClosed && context === "forecast") {
          setEditedRun(null);
          setComparedRun(null);
          const newPath = location.pathname.replace("_preview", "");
          navigate({
            pathname: newPath,
          });
        }
        setIsConnected(false);
        setIsAwaitingResponse(false);
        setIsComparing(false);
        setChatSessionId(null);
        if (intentionallyClosed) {
          setChatContext(null);
          conversationIdRef.current = null;
          conversationScopeRef.current = null;
        }
        setQueuedMessages(0);
        setQueuedPrompts([]);
        queuedPromptsRef.current = [];
        setIsStopRequested(false);
      });

      socket.on("connect_error", (err) => {
        console.error("Connection error:", err.message);
        console.warn(ServerURL.socket);
        setIsConnected(false);
      });

      socket.on("reconnect", (attemptNumber) => {
        console.log("Socket reconnected after", attemptNumber, "attempts");
        setIsConnected(true);
      });

      socket.on("reconnect_attempt", (attemptNumber) => {
        console.log("Attempting to reconnect:", attemptNumber);
      });

      // ============================================
      // Streaming Event Listeners for Bedrock Agent
      // ============================================

      socket.on("ai_queue_status", (data) => {
        const queuedCount = Number(data?.queued_count ?? 0);
        const running = Boolean(data?.is_running);
        setQueuedMessages(Number.isFinite(queuedCount) ? queuedCount : 0);
        // Only clear local queued prompts when queue is truly drained and idle.
        // If running=true and queued_count=0, one queued turn may have just started.
        if (!running && queuedCount === 0 && queuedPromptsRef.current.length > 0) {
          setQueuedPrompts([]);
          queuedPromptsRef.current = [];
        }
        if (!running && queuedCount <= 0 && agentStateRef.current.status !== "streaming") {
          setIsAwaitingResponse(false);
        }
      });

      socket.on("ai_stop_requested", (data) => {
        console.log("AI Stop Requested:", data);
        if (data?.clear_queue) {
          setQueuedPrompts([]);
          queuedPromptsRef.current = [];
        }
        setIsStopRequested(true);
      });

      // Agent started processing
      socket.on("ai_start", (data) => {
        console.log("AI Start:", data);
        setIsAwaitingResponse(true);
        setIsStopRequested(false);
        if (queuedPromptsRef.current.length > 0) {
          setQueuedPrompts((prev) => {
            if (prev.length === 0) return prev;
            const [nextPrompt, ...remaining] = prev;
            queuedPromptsRef.current = remaining;
            appendSentMessage(nextPrompt);
            return remaining;
          });
        }
        const newState: AgentState = {
          ...initialAgentState,
          status: "streaming",
          toolCalls: [], // Reset tool calls for new turn
        };
        setAgentState(newState);
        agentStateRef.current = newState;
      });

      // New iteration started
      socket.on("ai_iteration", (data) => {
        console.log(`AI Iteration: ${data.number}/${data.total}`);
        setAgentState((prev) => {
          const newState = {
            ...prev,
            currentIteration: data.number,
            maxIterations: data.total,
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // Agent reasoning/thinking
      socket.on("ai_reasoning", (data) => {
        console.log("AI Reasoning:", data.text?.substring(0, 100) + "...");
        setAgentState((prev) => {
          const newStep: ReasoningStep = {
            text: data.text,
            iteration: data.iteration,
          };
          const newState = {
            ...prev,
            reasoning: data.text,
            reasoningHistory: [...prev.reasoningHistory, newStep],
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // SQL query executed
      socket.on("ai_sql_executed", (data) => {
        console.log("AI SQL Executed:", data.sql?.substring(0, 100) + "...");
        console.log(`   Row count: ${data.row_count}`);
        setAgentState((prev) => {
          const newQuery: SQLQuery = {
            sql: data.sql,
            row_count: data.row_count,
            iteration: data.iteration,
            summary: data.summary,
            results: data.results,
            columns: data.columns,
          };
          const newState = {
            ...prev,
            sqlQueries: [...prev.sqlQueries, newQuery],
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // Documentation searched
      socket.on("ai_doc_searched", (data) => {
        console.log("[DEBUG] ai_doc_searched received:", {
          question: data.question,
          table_names: data.table_names,
          table_count: data.table_names?.length || 0,
          schema_info_length: data.schema_info?.length || 0,
          iteration: data.iteration,
        });
        setAgentState((prev) => {
          const newSearch: DocSearch = {
            question: data.question,
            schema_info: data.schema_info,
            table_names: data.table_names,
            documents_count: data.documents_count,
            iteration: data.iteration,
          };
          const newState = {
            ...prev,
            docSearches: [...prev.docSearches, newSearch],
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // Incremental completion deltas for lower time-to-first-token UX
      socket.on("ai_text_delta", (data) => {
        if (!data?.text || typeof data.text !== "string") return;
        setAgentState((prev) => {
          const newState = {
            ...prev,
            streamedText: prev.streamedText + data.text,
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // Chart generated
      socket.on("ai_chart_generated", (data) => {
        console.log("AI Chart Generated:", data.chart_type, data.status);
        console.log("   Chart URL present:", !!data.chart_url);
        console.log("   Chart URL length:", data.chart_url?.length || 0);
        setAgentState((prev) => {
          const newChart: ChartGenerated = {
            chart_url: data.chart_url,
            chart_type: data.chart_type,
            status: data.status,
            message: data.message,
            iteration: data.iteration,
          };
          const newState = {
            ...prev,
            charts: [...prev.charts, newChart],
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // ============================================
      // Supply Diagnosis Tool Call Event Handlers
      // ============================================

      // Register handlers for all tool call events
      TOOL_CALL_EVENTS.forEach((eventType) => {
        socket.on(eventType, (payload: { type: string; data: Record<string, unknown> } | Record<string, unknown>) => {
          // Handle both wrapped {type, data} and direct data payloads
          const data = ('data' in payload && typeof payload.data === 'object')
            ? (payload.data as Record<string, unknown>)
            : (payload as Record<string, unknown>);

          console.log(`Tool Call Event [${eventType}]:`, {
            toolName: SOCKET_EVENT_TO_TOOL[eventType],
            sku: data.sku,
            iteration: data.iteration,
          });

          const toolCall = createToolCallFromEvent(eventType, data);
          const genericTable = extractGenericTableFromEvent(eventType, data);

          setAgentState((prev) => {
            const nextTables = [...prev.genericTables];
            if (genericTable) {
              const fingerprint = getTableFingerprint(genericTable);
              const exists = nextTables.some(
                (table) => getTableFingerprint(table) === fingerprint
              );
              if (!exists) {
                nextTables.push(genericTable);
              }
            }
            const newState = {
              ...prev,
              toolCalls: [...prev.toolCalls, toolCall],
              genericTables: nextTables,
            };
            agentStateRef.current = newState;
            return newState;
          });
        });
      });

      // Additional table-capable events that are not mapped to visible tool cards
      ["ai_supply_runs_listed"].forEach((eventType) => {
        socket.on(eventType, (payload: { type: string; data: Record<string, unknown> } | Record<string, unknown>) => {
          const data = ('data' in payload && typeof payload.data === 'object')
            ? (payload.data as Record<string, unknown>)
            : (payload as Record<string, unknown>);
          const genericTable = extractGenericTableFromEvent(eventType, data);
          if (!genericTable) return;

          setAgentState((prev) => {
            const fingerprint = getTableFingerprint(genericTable);
            const exists = prev.genericTables.some(
              (table) => getTableFingerprint(table) === fingerprint
            );
            if (exists) return prev;

            const newState = {
              ...prev,
              genericTables: [...prev.genericTables, genericTable],
            };
            agentStateRef.current = newState;
            return newState;
          });
        });
      });

      // Final completion text
      socket.on("ai_completion", (data) => {
        console.log("AI Completion:", data.text?.substring(0, 100) + "...");
        // Completion is handled by the 'response' event
      });

      // Agent finished processing
      socket.on("ai_done", (data) => {
        console.log("AI Done:", data);
        setIsStopRequested(false);
        setAgentState((prev) => {
          const newState = {
            ...prev,
            status: "done" as const,
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // Agent cancelled by user request
      socket.on("ai_cancelled", (data) => {
        console.log("AI Cancelled:", data);
        setIsStopRequested(false);
      });

      // Agent error
      socket.on("ai_error", (data) => {
        console.error("AI Error:", data);
        setAgentState((prev) => {
          const newState = {
            ...prev,
            status: "error" as const,
            error: data.error,
          };
          agentStateRef.current = newState;
          return newState;
        });
      });

      // ============================================
      // Chat History Response Handlers
      // ============================================

      socket.on("chat_history_response", (data) => {
        console.log("Chat history response:", data);
        setChatHistoryLoading(false);
        if (data.history) {
          setChatHistory(data.history);
        }
        if (data.error) {
          console.error("Chat history error:", data.error);
        }
      });

      // ============================================
      // Final Response Handler
      // ============================================

      socket.on("response", (data) => {
        setIsAwaitingResponse(false);
        setIsStopRequested(false);

        console.log("Raw server response:", data);
        console.log("Response type:", typeof data);
        console.log("Response structure:", {
          hasType: data?.type,
          hasTitle: data?.title,
          hasTables: data?.tables,
          tablesLength: data?.tables?.length,
        });

        // Get the accumulated agent state from the ref
        const currentAgentState = agentStateRef.current;
        console.log("Agent state at response time:", {
          status: currentAgentState.status,
          sqlQueriesCount: currentAgentState.sqlQueries.length,
          chartsCount: currentAgentState.charts.length,
          charts: currentAgentState.charts,
        });

        let message: Message;

        if (data && typeof data === "object" && data.type === "table") {
          console.log("Creating table message with:", {
            title: data.title,
            tablesCount: data.tables?.length,
          });

          message = {
            type: "received",
            content: data.title || "",
            timestamp: new Date(),
            dataType: "table",
            tables: data.tables || [],
            sqlQueries: currentAgentState.sqlQueries.length > 0 ? currentAgentState.sqlQueries : undefined,
            charts: currentAgentState.charts.length > 0 ? currentAgentState.charts : undefined,
            // Persist full agent trace for "how did agent get here" display
            docSearches: currentAgentState.docSearches.length > 0 ? currentAgentState.docSearches : undefined,
            reasoningHistory: currentAgentState.reasoningHistory.length > 0 ? currentAgentState.reasoningHistory : undefined,
            toolCalls: currentAgentState.toolCalls.length > 0 ? currentAgentState.toolCalls : undefined,
            genericTables: currentAgentState.genericTables.length > 0 ? currentAgentState.genericTables : undefined,
            iterationCount: currentAgentState.currentIteration,
          };

          console.log("Created table message:", message);
        } else {
          let content = "";
          if (typeof data === "string") {
            content = data;
          } else if (data && data.message) {
            content = data.message;
          } else {
            content = JSON.stringify(data, null, 2);
          }
          if (!content && currentAgentState.streamedText.trim()) {
            content = currentAgentState.streamedText.trim();
          }

          message = {
            type: "received",
            content,
            timestamp: new Date(),
            dataType: "text",
            sqlQueries: currentAgentState.sqlQueries.length > 0 ? currentAgentState.sqlQueries : undefined,
            charts: currentAgentState.charts.length > 0 ? currentAgentState.charts : undefined,
            // Persist full agent trace for "how did agent get here" display
            docSearches: currentAgentState.docSearches.length > 0 ? currentAgentState.docSearches : undefined,
            reasoningHistory: currentAgentState.reasoningHistory.length > 0 ? currentAgentState.reasoningHistory : undefined,
            toolCalls: currentAgentState.toolCalls.length > 0 ? currentAgentState.toolCalls : undefined,
            genericTables: currentAgentState.genericTables.length > 0 ? currentAgentState.genericTables : undefined,
            iterationCount: currentAgentState.currentIteration,
          };

          console.log("Created text message:", message);
        }

        setMessages((prev) => [...prev, message]);

        // Reset agent state after response is complete
        setAgentState(initialAgentState);
        agentStateRef.current = initialAgentState;
      });
    },
    [
      appendSentMessage,
      authToken,
      selectedCustomerId,
      whoIAm,
      location.pathname,
      location.search,
      navigate,
      setComparedRun,
      setEditedRun,
      setIsComparing,
    ]
  );

  const disconnectChat = useCallback(() => {
    if (socketRef.current) {
      socketRef.current?.disconnect();
      setMessages([]);
      setIsConnected(false);
      setIsAwaitingResponse(false);
      setChatSessionId(null);
      setChatContext(null);
      setQueuedMessages(0);
      setQueuedPrompts([]);
      queuedPromptsRef.current = [];
      setIsStopRequested(false);
      conversationIdRef.current = null;
      conversationScopeRef.current = null;
    }
  }, [socketRef]);

  useEffect(() => {
    if (isConnected && socketCustomerIdRef.current && socketCustomerIdRef.current !== selectedCustomerId) {
      console.log(`Org changed from ${socketCustomerIdRef.current} to ${selectedCustomerId}, disconnecting chat`);
      disconnectChat();
    }
  }, [selectedCustomerId, isConnected, disconnectChat]);

  // Fetch recent chat history
  const fetchChatHistory = useCallback((limit: number = 10) => {
    if (!socketRef.current?.connected) {
      console.log("Socket not connected, cannot fetch chat history");
      return;
    }
    setChatHistoryLoading(true);
    socketRef.current.emit("get_chat_history", JSON.stringify({ limit }));
  }, []);

  // Load a conversation from history into the current chat
  const loadChatFromHistory = useCallback((historyItem: ChatHistoryItem) => {
    // Create messages from the history item
    const sentMessage: Message = {
      type: "sent",
      content: historyItem.query,
      timestamp: new Date(historyItem.timestamp),
    };

    const receivedMessage: Message = {
      type: "received",
      content: historyItem.response,
      timestamp: new Date(historyItem.timestamp),
      dataType: "text",
      sqlQueries: historyItem.sql_queries,
      docSearches: historyItem.doc_searches,
      charts: historyItem.charts,
      iterationCount: historyItem.iterations,
    };

    // Set messages (this replaces current messages)
    setMessages([sentMessage, receivedMessage]);
  }, []);

  useEffect(() => {
    if (comparedRun) {
      setIsComparing(true);
    } else {
      setIsComparing(false);
    }
  }, [setIsComparing, comparedRun]);

  // Debug: Log when agentState changes in Provider
  useEffect(() => {
    console.log("PROVIDER useEffect - agentState changed:", {
      status: agentState.status,
      iteration: agentState.currentIteration,
      chartsCount: agentState.charts.length,
    });
  }, [agentState]);

  const value: ChatContextType = {
    isConnected,
    messages,
    setMessages,
    sendMessage,
    connectChat,
    disconnectChat,
    isAwaitingResponse,
    chatSessionId,
    chatContext,
    agentState,
    // Chat history
    chatHistory,
    chatHistoryLoading,
    fetchChatHistory,
    loadChatFromHistory,
    stopCurrentResponse,
    queuedMessages,
    queuedPrompts,
    isStopRequested,
  };

  console.log("ChatProvider RENDER - agentState.status:", agentState.status);

  return <Provider value={value}>{props.children}</Provider>;
};
